/**
 * research.js — Research AI tasks (competitors, market sizing, consumer insights, reviews, trends).
 * Schema and quality bar: see _exemplar.js.
 *
 * Template note: optional bullet lines use the tight form
 *   {{#if field}}- Label: {{field}}
 *   {{/if}}
 * so empty fields never leave blank lines between bullets.
 */
export const RESEARCH_TASKS = [
  {
    id: "research-competitor",
    category: "research",
    title: "Research Competitors",
    description: "A sourced, side-by-side profile of each competitor — positioning, pricing, pack sizes, distribution, claims, review themes, marketing and weaknesses — plus takeaways for your brand.",
    outputType: "text",
    recommendedTools: ["perplexity-research", "gemini-research", "chatgpt"],
    estimatedTime: "2 min to fill · 5–10 min in Perplexity Deep Research",
    fields: [
      { id: "brandName", label: "Brand name", type: "text", placeholder: "XYZ", required: true, brandKey: "brandName" },
      { id: "category", label: "Product category", type: "text", placeholder: "Energy drink", required: true, brandKey: "category" },
      { id: "competitors", label: "Competitors to research", type: "tags", placeholder: "Celsius, Alani Nu, Ghost", required: true, brandKey: "competitors", span: "full" },
      { id: "positioning", label: "Brand positioning", type: "textarea", placeholder: "Natural premium performance energy", required: true, brandKey: "positioning", span: "full" },
      { id: "targetConsumer", label: "Target consumer", type: "textarea", placeholder: "Men and women 18–35 interested in fitness", brandKey: "targetConsumer", span: "full" },
      { id: "claims", label: "Key claims", type: "tags", placeholder: "Zero sugar, Natural caffeine, No artificial colors", brandKey: "claims" },
      { id: "pricePoint", label: "Your retail price point", type: "text", placeholder: "$2.99 per can", brandKey: "pricePoint" },
      { id: "channels", label: "Your sales channels", type: "tags", placeholder: "DTC, Amazon, Natural grocery", brandKey: "channels" },
      { id: "market", label: "Market to research", type: "select", options: ["U.S. national", "U.S. natural / specialty", "U.S. convenience", "Canada", "UK / Europe", "Online only (Amazon + DTC)"], default: "U.S. national" },
      { id: "depth", label: "Depth", type: "select", options: ["Quick scan (1 paragraph each)", "Full profile (all sections)"], default: "Full profile (all sections)" },
      { id: "focus", label: "Extra focus area (optional)", type: "select", options: ["None", "Pricing & promotions", "Retail distribution", "Marketing & social", "Product & claims", "Customer reviews"], default: "None" },
    ],
    template: `Act as a senior CPG competitive-intelligence analyst who has built competitor dossiers for {{category|lower}} brands pitching Whole Foods, Target and Amazon.

Research the following competitors of {{brandName}}, {{category|article}} sold in {{market}}, and profile each one. Use current, public sources and cite them.

COMPETITORS TO PROFILE
{{competitors|list}}

OUR BRAND (for contrast, do not profile us)
- Positioning: {{positioning}}
{{#if targetConsumer}}- Target consumer: {{targetConsumer}}
{{/if}}{{#if claims}}- Key claims: {{claims|join}}
{{/if}}{{#if pricePoint}}- Price point: {{pricePoint}}
{{/if}}{{#if channels}}- Channels: {{channels|join}}
{{/if}}

DEPTH
{{depth}}
{{#if focus!=None}}
Go deepest on: {{focus}}.
{{/if}}

FOR EACH COMPETITOR PROVIDE
1. One-line positioning statement (in their words if available, with source)
2. Target consumer and the occasion they own
3. Product line: SKUs, flavors / variants, pack sizes and formats
4. Pricing: shelf price, price per unit and per oz (or per serving), typical promo depth
5. Distribution: retailers and channels they are in, estimated door count if published
6. Claims and certifications on pack (e.g. organic, non-GMO, zero sugar)
7. Marketing: hero message, social following, influencer / athlete partnerships, notable campaigns
8. Customer review themes: top 3 praises and top 3 complaints (Amazon, retailer sites, Reddit)
9. Funding, ownership and recent news (last 12 months)
10. Weaknesses and gaps {{brandName}} could exploit

THEN PROVIDE
11. Comparison table: one row per competitor (plus {{brandName}}) with columns for positioning, hero claim, price per unit, pack sizes, key channels, primary weakness
12. Five strategic takeaways for {{brandName}}, each tied to a specific finding
13. Three questions a retail buyer would ask {{brandName}} about this competitive set, with suggested answers

RULES
- Cite a source (URL and date) for every price, door count, funding figure and review theme.
- If a data point is not publicly available, write "not found" — never estimate without labeling it an estimate.
- Prices vary by retailer and date; state which retailer and date each price came from.
- Keep each profile under 250 words{{#if depth=Quick scan (1 paragraph each)}} (one paragraph per competitor){{/if}}.`,
    tips: [
      "Run this in Perplexity Deep Research or Gemini Deep Research so every fact is cited. Then paste the comparison table into Google Sheets.",
      "Follow up: “Now write a one-page battle card for my sales team against [strongest competitor].”",
      "Feed the weaknesses (#10) into the Research AI Positioning Map prompt.",
    ],
  },

  {
    id: "research-market-analysis",
    category: "research",
    title: "Market Analysis",
    description: "A cited market report on your category — size, growth, segments, channels, key players, tailwinds and headwinds — formatted for a deck or investor memo.",
    outputType: "text",
    recommendedTools: ["perplexity-research", "gemini-research", "chatgpt"],
    estimatedTime: "2 min to fill · 5–15 min in Deep Research",
    fields: [
      { id: "brandName", label: "Brand name", type: "text", placeholder: "XYZ", required: true, brandKey: "brandName" },
      { id: "category", label: "Product category", type: "text", placeholder: "Energy drink", required: true, brandKey: "category" },
      { id: "productDescription", label: "Product description", type: "textarea", placeholder: "12 oz sleek can, zero sugar, 200 mg natural caffeine", required: true, brandKey: "productDescription", span: "full" },
      { id: "targetConsumer", label: "Target consumer", type: "textarea", placeholder: "Men and women 18–35 interested in fitness", brandKey: "targetConsumer", span: "full" },
      { id: "positioning", label: "Brand positioning", type: "textarea", placeholder: "Natural premium performance energy", brandKey: "positioning", span: "full" },
      { id: "competitors", label: "Known competitors", type: "tags", placeholder: "Celsius, Alani Nu, Ghost", brandKey: "competitors" },
      { id: "channels", label: "Your sales channels", type: "tags", placeholder: "DTC, Amazon, Natural grocery", brandKey: "channels" },
      { id: "stage", label: "Business stage", type: "select", options: ["Idea / pre-launch", "Launched — DTC only", "In local / regional retail", "In national retail", "Scaling / raising capital"], brandKey: "stage" },
      { id: "market", label: "Geography", type: "select", options: ["United States", "U.S. + Canada", "North America + UK", "UK / Europe", "Global"], default: "United States" },
      { id: "purpose", label: "I need this for", type: "select", options: ["Investor deck / fundraising", "Retail buyer pitch", "Internal strategy planning", "Business plan / loan application"], default: "Investor deck / fundraising" },
      { id: "horizon", label: "Forecast horizon", type: "select", options: ["3 years", "5 years"], default: "5 years" },
    ],
    template: `Act as a senior CPG market-research analyst who has written category reports for SPINS, Circana and Mintel clients and knows how investors and retail buyers read market data.

Write a market analysis of the {{category|lower}} category in {{market}} for {{brandName}}. The report will be used for: {{purpose}}.

ABOUT THE BRAND
- Product: {{productDescription}}
{{#if positioning}}- Positioning: {{positioning}}
{{/if}}{{#if targetConsumer}}- Target consumer: {{targetConsumer}}
{{/if}}{{#if stage}}- Stage: {{stage}}
{{/if}}{{#if channels}}- Channels: {{channels|join}}
{{/if}}{{#if competitors}}- Competitors we track: {{competitors|join}}
{{/if}}

DELIVERABLES
1. Executive summary (5 bullets a reader can lift straight into a slide)
2. Market size: current retail sales value and volume, with the source, year and methodology for each figure; reconcile differences if sources disagree
3. Growth: historical CAGR (last 3–5 years) and projected CAGR over the next {{horizon}}, with sources
4. Segmentation table: segments by product type, price tier, need state and consumer group, with approximate share and growth of each
5. Channel breakdown: share of sales by channel (conventional grocery, natural / specialty, mass, club, convenience, e-commerce / Amazon, DTC, foodservice) and which channels are gaining
6. Key players table: top 8–10 brands with parent company, estimated share or sales, price tier, recent moves; flag emerging challenger brands separately
7. Tailwinds: consumer, regulatory, retail, ingredient and macro trends helping the category
8. Headwinds and risks: commoditization, regulation, input costs, retailer consolidation, private label
9. Where {{brandName}} fits: the specific segment, channel and consumer pocket where its positioning is most defensible, with a realistic serviceable-addressable-market estimate and the math behind it
10. Three charts described as chart-ready tables (year-by-year sales, segment share, channel share) that can be pasted into Google Sheets
11. Source list with URLs and publication dates

FORMAT
- Use headers that match the deliverable numbers so sections can be lifted into slides.
- Put every table in plain columns (no merged cells) so it pastes into Google Sheets.
- Beside each market-size and growth figure, add a confidence rating (high / medium / low) and why.

RULES
- Cite every number. Prefer primary sources (SPINS, Circana / IRI, NielsenIQ, Mintel, Euromonitor, company filings, trade press) over blog aggregators.
- Label clearly when a figure is an estimate and show how you derived it.
- Do not invent market-size numbers to fill gaps; write "no public figure found" and suggest a proxy.
- Write for {{purpose}}: concise, quantitative, no marketing fluff.
- Total length 900–1,400 words plus tables.`,
    tips: [
      "Use Perplexity Deep Research or Gemini Deep Research — both will browse dozens of sources and cite them.",
      "Paste the market-size slide bullets (#1) and the SAM math (#9) into the Investor AI pitch-deck prompt.",
      "Follow up: “Turn section 10 into three charts I can export as images.”",
    ],
  },

  {
    id: "research-consumer-insights",
    category: "research",
    title: "Consumer Insights",
    description: "Evidence-based buyer personas with jobs-to-be-done, purchase drivers and barriers, usage occasions and the exact language your consumers use.",
    outputType: "text",
    recommendedTools: ["perplexity-research", "gemini-research", "chatgpt"],
    estimatedTime: "2 min to fill · 5 min in Deep Research",
    fields: [
      { id: "brandName", label: "Brand name", type: "text", placeholder: "XYZ", required: true, brandKey: "brandName" },
      { id: "category", label: "Product category", type: "text", placeholder: "Energy drink", required: true, brandKey: "category" },
      { id: "productDescription", label: "Product description", type: "textarea", placeholder: "12 oz sleek can, zero sugar, 200 mg natural caffeine", required: true, brandKey: "productDescription", span: "full" },
      { id: "targetConsumer", label: "Target consumer (your current hypothesis)", type: "textarea", placeholder: "Men and women 18–35 interested in fitness", required: true, brandKey: "targetConsumer", span: "full" },
      { id: "positioning", label: "Brand positioning", type: "textarea", placeholder: "Natural premium performance energy", brandKey: "positioning", span: "full" },
      { id: "claims", label: "Key claims", type: "tags", placeholder: "Zero sugar, Natural caffeine, No artificial colors", brandKey: "claims" },
      { id: "pricePoint", label: "Retail price point", type: "text", placeholder: "$2.99 per can", brandKey: "pricePoint" },
      { id: "competitors", label: "Competitors", type: "tags", placeholder: "Celsius, Alani Nu, Ghost", brandKey: "competitors" },
      { id: "personaCount", label: "Number of personas", type: "select", options: ["2", "3", "4"], default: "3" },
      { id: "sources", label: "Research sources", type: "select", options: ["Public web (reviews, Reddit, social, surveys, trade press)", "Public web + my pasted data below"], default: "Public web (reviews, Reddit, social, surveys, trade press)" },
      { id: "pasteContent", label: "Your own data (optional)", type: "textarea", placeholder: "Paste survey answers, customer emails, DMs, interview notes or reviews", help: "Anything real from your customers makes the personas far more accurate.", span: "full" },
    ],
    template: `Act as a senior consumer-insights lead who has run shopper research for {{category|lower}} brands and translates it into personas that marketing, sales and product teams actually use.

Build {{personaCount}} consumer personas for {{brandName}}, {{category|article}}.

PRODUCT
{{productDescription}}

OUR CURRENT HYPOTHESIS OF THE TARGET CONSUMER
{{targetConsumer}}
Challenge this hypothesis where the evidence disagrees.

BRAND CONTEXT
- Brand: {{brandName}}, {{category|lower}}
{{#if positioning}}- Positioning: {{positioning}}
{{/if}}{{#if claims}}- Key claims: {{claims|join}}
{{/if}}{{#if pricePoint}}- Price point: {{pricePoint}}
{{/if}}{{#if competitors}}- Competitors whose buyers we can learn from: {{competitors|join}}
{{/if}}

EVIDENCE BASE
{{sources}}. Draw on product reviews (ours and competitors'), Reddit and forum threads, social comments, published surveys and trade press. Quote real consumer language where possible and cite where it came from.
{{#if pasteContent}}

OUR OWN DATA
{{pasteContent}}
{{/if}}

FOR EACH PERSONA PROVIDE
1. Name, age range, life stage, household, income band, where they shop
2. Jobs-to-be-done: the functional, emotional and social jobs they hire {{category|article}} for
3. Usage occasions: when, where, how often, with whom, what it replaces
4. Purchase drivers ranked (taste, function, price, ingredients, brand, convenience, etc.) with evidence
5. Barriers and objections, and what would overcome each
6. Current alternatives and what frustrates them about those alternatives
7. Media and discovery: where they learn about new products, who they trust
8. Language they use: 8–10 verbatim-style phrases for how they describe the need, the product and the benefit
9. Messaging implication: the single most persuasive message for this persona, in their words

THEN PROVIDE
10. Persona comparison table (one row per persona; columns: primary job, top driver, top barrier, best channel, best message)
11. Which persona {{brandName}} should lead with and why, plus a 3-question survey to confirm it
12. Three places where the evidence contradicts our hypothesis

RULES
- Ground every persona in cited evidence; mark anything inferred as "inference".
- No stereotypes or demographic clichés without supporting data.
- Do not invent statistics; say "no data found" instead.
- Keep the whole output under 1,500 words.`,
    tips: [
      "Paste your real customer emails, DMs or survey answers into the optional data box — even 20 real comments sharply improve accuracy.",
      "Follow up: “Write three Meta ad hooks for persona 1 using only the verbatim phrases in section 8.”",
      "Use section 11's survey in the Research AI Build Consumer Survey prompt.",
    ],
  },

  {
    id: "research-pricing-comparison",
    category: "research",
    title: "Pricing Comparison",
    description: "A price-per-unit and price-per-oz table across competitors and channels, a price index against the category, and a recommended price ladder for your brand.",
    outputType: "text",
    recommendedTools: ["perplexity-research", "gemini-research", "chatgpt"],
    estimatedTime: "2 min to fill · 5 min in Deep Research",
    fields: [
      { id: "brandName", label: "Brand name", type: "text", placeholder: "XYZ", required: true, brandKey: "brandName" },
      { id: "category", label: "Product category", type: "text", placeholder: "Energy drink", required: true, brandKey: "category" },
      { id: "productDescription", label: "Product description (include pack size)", type: "textarea", placeholder: "12 oz sleek can, zero sugar, sold as singles and 12-packs", required: true, brandKey: "productDescription", span: "full" },
      { id: "pricePoint", label: "Your current or planned price", type: "text", placeholder: "$2.99 per can; $29.99 per 12-pack DTC", required: true, brandKey: "pricePoint" },
      { id: "competitors", label: "Competitors to compare", type: "tags", placeholder: "Celsius, Alani Nu, Ghost", required: true, brandKey: "competitors", span: "full" },
      { id: "positioning", label: "Brand positioning", type: "textarea", placeholder: "Natural premium performance energy", brandKey: "positioning", span: "full" },
      { id: "channels", label: "Your sales channels", type: "tags", placeholder: "DTC, Amazon, Natural grocery", brandKey: "channels" },
      { id: "unitBasis", label: "Compare price per", type: "select", options: ["fl oz", "oz (weight)", "unit / each", "serving", "gram of protein", "100 g"], default: "fl oz" },
      { id: "channelsToCompare", label: "Channels to price-check", type: "tags", placeholder: "Amazon, Walmart, Target, Whole Foods, Brand DTC site", default: "Amazon, Walmart, Target, Whole Foods, Brand DTC site" },
      { id: "priceTier", label: "Intended price tier", type: "select", options: ["Value", "Mainstream", "Premium", "Super-premium"], default: "Premium" },
    ],
    template: `Act as a senior CPG pricing strategist who has set shelf pricing and price-pack architecture for {{category|lower}} brands across grocery, mass, Amazon and DTC.

Build a pricing comparison for {{brandName}}, {{category|article}} positioned as {{priceTier|lower}}.

OUR PRODUCT AND PRICE
- Product: {{productDescription}}
- Current / planned price: {{pricePoint}}
{{#if positioning}}- Positioning: {{positioning}}
{{/if}}{{#if channels}}- Where we sell: {{channels|join}}
{{/if}}

COMPETITORS
{{competitors|list}}

CHANNELS TO CHECK
{{channelsToCompare|list}}

CONVERSION RULES
- Derive our pack size from the product description above; if it is ambiguous, state the assumption you used.
- Use the everyday (non-promo) shelf price as the base figure and list promo prices separately.
- Show the per-{{unitBasis}} math for at least one row so I can check it.

DELIVERABLES
1. Price table: one row per competitor SKU and pack size, one column per channel, showing shelf price, pack size, price per {{unitBasis}}, and the date and source of each price. Include {{brandName}} as the final row.
2. Price-per-{{unitBasis}} ranking from lowest to highest, with a category average and median.
3. Price index: {{brandName}} vs. the category average (100 = average) per channel.
4. Price-pack architecture observed: which pack sizes exist (single, 4-pack, 12-pack, variety), typical multipack discount %, and where each competitor's entry price sits.
5. Promo patterns: typical promo depth and frequency by competitor and channel (e.g. "2 for $5 every 4 weeks at Target").
6. Price ladder recommendation for {{brandName}}: suggested everyday price and promo price per channel, the multipack discount structure, and the gap to maintain versus the closest premium and closest value competitor.
7. Price-gap logic: the maximum premium a {{priceTier|lower}} {{category|lower}} can hold over the category leader before velocity typically suffers, with your reasoning and any cited evidence.
8. Risks: channel conflict (DTC vs. Amazon vs. retail MAP), and three questions to confirm with a broker or category manager.

RULES
- Cite the source URL and date for every price; prices change weekly, so label the date.
- If a price is not visible online, write "not found" rather than guessing.
- Convert all prices to price per {{unitBasis}} with the math shown.
- Output every table in a format that pastes cleanly into Google Sheets (plain columns, no merged cells).
- Flag any recommendation that touches MAP policy or retailer contract terms for review with a legal or retail adviser.`,
    tips: [
      "Run in Perplexity Deep Research, then paste table #1 into Google Sheets and keep it updated monthly.",
      "Follow up: “Model the margin impact of each price-ladder option at 40% retailer margin and 27% distributor margin.”",
      "Feed the recommended ladder into the Finance AI Build Pricing Model prompt.",
    ],
  },

  {
    id: "research-reviews-analysis",
    category: "research",
    title: "Analyze Customer Reviews",
    description: "Turn a pile of pasted reviews into ranked themes, sentiment scores, feature requests, messaging gold you can quote and a prioritized product-fix list.",
    outputType: "text",
    recommendedTools: ["chatgpt", "claude", "gemini"],
    estimatedTime: "3 min to fill · 1 min in ChatGPT",
    fields: [
      { id: "brandName", label: "Brand name", type: "text", placeholder: "XYZ", required: true, brandKey: "brandName" },
      { id: "category", label: "Product category", type: "text", placeholder: "Energy drink", required: true, brandKey: "category" },
      { id: "pasteContent", label: "Paste the reviews", type: "textarea", placeholder: "Paste 20–500 reviews here (Amazon, Shopify, retailer sites, DMs). Include star ratings if you have them.", required: true, span: "full", help: "More is better. ChatGPT and Claude handle hundreds of reviews in one paste." },
      { id: "reviewSource", label: "Whose reviews are these?", type: "select", options: ["My product", "A competitor's product", "A mix of mine and competitors'"], default: "My product" },
      { id: "competitorName", label: "Competitor name (if competitor reviews)", type: "text", placeholder: "Celsius" },
      { id: "productDescription", label: "Product description", type: "textarea", placeholder: "12 oz sleek can, zero sugar, 200 mg natural caffeine", brandKey: "productDescription", span: "full" },
      { id: "positioning", label: "Brand positioning", type: "textarea", placeholder: "Natural premium performance energy", brandKey: "positioning", span: "full" },
      { id: "targetConsumer", label: "Target consumer", type: "textarea", placeholder: "Men and women 18–35 interested in fitness", brandKey: "targetConsumer" },
      { id: "claims", label: "Key claims", type: "tags", placeholder: "Zero sugar, Natural caffeine, No artificial colors", brandKey: "claims" },
      { id: "goal", label: "Primary goal", type: "select", options: ["Improve the product", "Sharpen marketing messages", "Find competitor weaknesses", "Prepare for a retail buyer meeting"], default: "Sharpen marketing messages" },
    ],
    template: `Act as a senior voice-of-customer analyst who has coded thousands of reviews for {{category|lower}} brands and turned them into product roadmaps and ad copy.

Analyze the customer reviews below for {{brandName}}, {{category|article}}. Primary goal: {{goal|lower}}.

REVIEW SOURCE
{{reviewSource}}{{#if competitorName}} — competitor: {{competitorName}}{{/if}}

BRAND CONTEXT
- Brand: {{brandName}}, {{category|lower}}
{{#if productDescription}}- Product: {{productDescription}}
{{/if}}{{#if positioning}}- Positioning we want to own: {{positioning}}
{{/if}}{{#if targetConsumer}}- Target consumer: {{targetConsumer}}
{{/if}}{{#if claims}}- Claims we make: {{claims|join}}
{{/if}}

REVIEWS
"""
{{pasteContent}}
"""

DELIVERABLES
1. Overview: number of reviews analyzed, rating distribution if available, overall sentiment split (% positive / neutral / negative)
2. Theme table: every recurring theme with count, % of reviews, sentiment (+ / – / mixed), and one representative quote. Sort by frequency.
3. Scorecard (1–10, with one-line justification each): taste / sensory, effectiveness or function, value for money, packaging and format, shipping / availability, brand trust. Score only what the reviews support; mark "insufficient data" otherwise.
4. Top 5 praises and top 5 complaints, ranked, each with 2 verbatim quotes
5. Feature requests and unmet needs, with how often each appears
6. Messaging gold: 10 verbatim phrases that could be used (with permission) in ads, on the website or on pack, and the claim each supports
7. Claim check: which of our claims reviewers confirm, which they doubt, and which they never mention
8. Product and experience fixes, prioritized by frequency x severity, with a suggested owner (product, ops, marketing, customer service)
9. Persona signals: who is writing these reviews (life stage, occasion, purchase trigger) based on the text
{{#if reviewSource!=My product}}10. Competitive angle: the three weaknesses in these reviews {{brandName}} can credibly attack, and the exact message for each
{{/if}}{{#if reviewSource=My product}}10. Reply templates: a 3-sentence response for the most common complaint and the most common praise
{{/if}}

RULES
- Use only what is in the reviews; do not add outside facts or invent quotes.
- Quote verbatim with the star rating when present.
- Flag any quote that makes a health, safety or efficacy claim — it may need legal review before use in marketing.
- Output tables in a format that pastes cleanly into Google Sheets.`,
    tips: [
      "Export Amazon reviews with a free review-scraper extension or copy them page by page; 50+ reviews gives reliable themes.",
      "Follow up: “Write five Meta ad headlines using only the messaging gold in section 6.”",
      "Run it again with a competitor's reviews and choose “A competitor's product” to find their weaknesses.",
    ],
  },

  {
    id: "research-category-opportunities",
    category: "research",
    title: "Find Category Opportunities",
    description: "A scored opportunity map — whitespace, underserved needs, adjacent categories and format innovations — with the three bets most worth pursuing.",
    outputType: "text",
    recommendedTools: ["perplexity-research", "gemini-research", "chatgpt"],
    estimatedTime: "2 min to fill · 5–10 min in Deep Research",
    fields: [
      { id: "brandName", label: "Brand name", type: "text", placeholder: "XYZ", required: true, brandKey: "brandName" },
      { id: "category", label: "Product category", type: "text", placeholder: "Energy drink", required: true, brandKey: "category" },
      { id: "productDescription", label: "Product description", type: "textarea", placeholder: "12 oz sleek can, zero sugar, 200 mg natural caffeine", required: true, brandKey: "productDescription", span: "full" },
      { id: "positioning", label: "Brand positioning", type: "textarea", placeholder: "Natural premium performance energy", required: true, brandKey: "positioning", span: "full" },
      { id: "targetConsumer", label: "Target consumer", type: "textarea", placeholder: "Men and women 18–35 interested in fitness", brandKey: "targetConsumer", span: "full" },
      { id: "competitors", label: "Competitors", type: "tags", placeholder: "Celsius, Alani Nu, Ghost", brandKey: "competitors" },
      { id: "ingredients", label: "Hero ingredients", type: "tags", placeholder: "Green coffee, L-theanine", brandKey: "ingredients" },
      { id: "channels", label: "Sales channels", type: "tags", placeholder: "DTC, Amazon, Natural grocery", brandKey: "channels" },
      { id: "stage", label: "Business stage", type: "select", options: ["Idea / pre-launch", "Launched — DTC only", "In local / regional retail", "In national retail", "Scaling / raising capital"], brandKey: "stage" },
      { id: "riskAppetite", label: "Risk appetite", type: "select", options: ["Close-in (line extensions, new sizes, new channels)", "Balanced", "Bold (new categories, new formats, new consumers)"], default: "Balanced" },
      { id: "opportunityCount", label: "Opportunities to generate", type: "select", options: ["6", "10", "15"], default: "10" },
    ],
    template: `Act as a senior CPG innovation strategist who has led whitespace and category-extension work for emerging {{category|lower}} brands that later sold to strategics.

Identify and score {{opportunityCount}} growth opportunities for {{brandName}}, {{category|article}}. Risk appetite: {{riskAppetite}}.

WHERE WE ARE TODAY
- Product: {{productDescription}}
- Positioning: {{positioning}}
{{#if targetConsumer}}- Target consumer: {{targetConsumer}}
{{/if}}{{#if stage}}- Stage: {{stage}}
{{/if}}{{#if channels}}- Channels: {{channels|join}}
{{/if}}{{#if ingredients}}- Hero ingredients: {{ingredients|join}}
{{/if}}{{#if competitors}}- Competitors: {{competitors|join}}
{{/if}}

SEARCH THESE FOUR LENSES
A. Whitespace inside the category: price tiers, pack sizes, flavors, need states or consumers that no strong brand currently owns
B. Underserved needs: complaints and unmet requests visible in reviews, Reddit, social and trade press
C. Adjacent categories: where our brand equity, ingredients or consumer would travel credibly
D. Format and occasion innovation: new formats (e.g. powder, shot, bar, concentrate, multipack, foodservice) or new occasions for the same product

DELIVERABLES
1. Category landscape snapshot: 5 bullets on how the category is segmented today and where growth is coming from, with sources
2. Opportunity table: {{opportunityCount}} opportunities, one row each, with columns: lens (A–D), description, evidence (with source), target consumer, estimated size or demand signal, difficulty (1–5), fit with our positioning (1–5), time to market, capital required (low / medium / high)
3. Scoring: total score per opportunity and a ranked list, using this rule so I can rework it in a spreadsheet:
   total = (fit x 2) + (demand signal strength 1–5 x 2) - difficulty - capital (low = 1, medium = 2, high = 3)
4. Deep dive on the top 3: concept in one sentence, why now, who buys it, what it replaces, price and margin hypothesis, first channel, what we would need to build or source, and the biggest risk
5. What to kill: opportunities that look attractive but conflict with our positioning or cannibalize the core, and why
6. Validation plan: the cheapest test for each of the top 3 (e.g. landing-page test, DTC pre-sale, farmers-market pilot, retailer concept test) with a go / no-go metric
7. Five questions to ask a category buyer or distributor to pressure-test the top opportunity

RULES
- Cite evidence for every demand signal; label estimates and show the reasoning.
- Respect the risk appetite: {{riskAppetite}}.
- Do not recommend anything that requires claims we cannot substantiate; flag any regulated claim (health, functional, organic) for regulatory review.
- Keep it decision-ready: no generic advice, every opportunity must be specific to {{brandName}}.`,
    tips: [
      "Run it in Perplexity Deep Research so the demand signals are cited, then paste the scoring table into Google Sheets and adjust the weights.",
      "Follow up: “Write a one-page concept brief for opportunity #1 that I can show a co-packer and a buyer.”",
      "Use the top concept in the Packaging AI Create Packaging Concept prompt to visualize it.",
    ],
  },

  {
    id: "research-trends",
    category: "research",
    title: "Trend Report",
    description: "A cited trend report covering ingredient, format, claim, channel and culture trends in your category, each rated by maturity with a clear action for your brand.",
    outputType: "text",
    recommendedTools: ["perplexity-research", "gemini-research", "chatgpt"],
    estimatedTime: "1 min to fill · 5–10 min in Deep Research",
    fields: [
      { id: "brandName", label: "Brand name", type: "text", placeholder: "XYZ", required: true, brandKey: "brandName" },
      { id: "category", label: "Product category", type: "text", placeholder: "Energy drink", required: true, brandKey: "category" },
      { id: "targetConsumer", label: "Target consumer", type: "textarea", placeholder: "Men and women 18–35 interested in fitness", required: true, brandKey: "targetConsumer", span: "full" },
      { id: "positioning", label: "Brand positioning", type: "textarea", placeholder: "Natural premium performance energy", brandKey: "positioning", span: "full" },
      { id: "claims", label: "Key claims", type: "tags", placeholder: "Zero sugar, Natural caffeine, No artificial colors", brandKey: "claims" },
      { id: "ingredients", label: "Hero ingredients", type: "tags", placeholder: "Green coffee, L-theanine", brandKey: "ingredients" },
      { id: "channels", label: "Sales channels", type: "tags", placeholder: "DTC, Amazon, Natural grocery", brandKey: "channels" },
      { id: "timeframe", label: "Look-back window", type: "select", options: ["Last 6 months", "Last 12 months", "Last 24 months"], default: "Last 12 months" },
      { id: "region", label: "Region", type: "select", options: ["United States", "North America", "U.S. + UK / Europe", "Global"], default: "United States" },
      { id: "lenses", label: "Trend lenses", type: "tags", placeholder: "Ingredient, Format, Claim, Channel, Culture", default: "Ingredient, Format, Claim, Channel, Culture" },
    ],
    template: `Act as a senior CPG trend analyst who tracks Expo West, Expo East, SPINS data, TikTok and retailer new-item reports for {{category|lower}} brands.

Write a trend report for {{brandName}}, {{category|article}}, covering {{region}} over the {{timeframe|lower}}.

WHO WE SERVE
- Target consumer: {{targetConsumer}}
{{#if positioning}}- Positioning: {{positioning}}
{{/if}}{{#if claims}}- Current claims: {{claims|join}}
{{/if}}{{#if ingredients}}- Hero ingredients: {{ingredients|join}}
{{/if}}{{#if channels}}- Channels: {{channels|join}}
{{/if}}

TREND LENSES TO COVER
{{lenses|list}}

MATURITY SCALE (use exactly these labels)
- Emerging: early signals, few products, mostly DTC / social
- Growing: multiple brands, natural and specialty distribution, rising search volume
- Mainstream: mass retail, private label entering
- Fading: declining interest, discounting, delistings

DELIVERABLES
1. Headline: the 3 trends that matter most for {{brandName}} right now, one sentence each
2. Trend table: 12–18 trends across all lenses with columns: lens, trend, maturity, evidence (with source and date), example brands or products, relevance to {{brandName}} (high / medium / low)
3. For every high-relevance trend: what it means for our product, claims, packaging, channel or content, and the specific move we could make in the next 90 days
4. Ingredient watch: ingredients gaining and losing momentum in {{category|lower}}, with regulatory notes where relevant (e.g. novel ingredients, GRAS status, state restrictions)
5. Claim watch: which on-pack claims are rising or becoming table stakes, and which are drawing regulatory or consumer skepticism
6. Channel and culture: shifts in where and how {{targetConsumer}} discover and buy, including creators, retail media and store formats
7. Counter-trends and risks: what could reverse, and which trends are hype
8. Act / watch / ignore: sort all trends into three lists for {{brandName}} with a one-line reason each
9. Source list with URLs and dates

RULES
- Cite a dated source for every trend; trade press (Food Dive, BevNET, Nosh, NutraIngredients), SPINS / Circana releases, Google Trends and retailer announcements are preferred.
- Do not invent statistics; if a trend is anecdotal, say so.
- Flag any health, functional or ingredient claim trend that would require substantiation or regulatory review before {{brandName}} adopts it.
- Keep the report under 1,500 words plus the table.`,
    tips: [
      "Run in Perplexity Deep Research or Gemini Deep Research for cited, current sources.",
      "Follow up: “For the #1 Act trend, draft a 30-day social content plan that ties it to our product.”",
      "Repeat quarterly and keep the trend table in Google Sheets to see what moves between maturity stages.",
    ],
  },

  {
    id: "research-positioning-map",
    category: "research",
    title: "Build Positioning Map",
    description: "A positioning map with justified axes, every competitor placed with evidence, the whitespace identified and a recommended position for your brand with proof points.",
    outputType: "text",
    recommendedTools: ["perplexity-research", "chatgpt", "claude"],
    estimatedTime: "2 min to fill · 3 min in Perplexity or ChatGPT",
    fields: [
      { id: "brandName", label: "Brand name", type: "text", placeholder: "XYZ", required: true, brandKey: "brandName" },
      { id: "category", label: "Product category", type: "text", placeholder: "Energy drink", required: true, brandKey: "category" },
      { id: "competitors", label: "Competitors to place", type: "tags", placeholder: "Celsius, Alani Nu, Ghost, Red Bull, Monster", required: true, brandKey: "competitors", span: "full" },
      { id: "positioning", label: "Brand positioning", type: "textarea", placeholder: "Natural premium performance energy", required: true, brandKey: "positioning", span: "full" },
      { id: "targetConsumer", label: "Target consumer", type: "textarea", placeholder: "Men and women 18–35 interested in fitness", brandKey: "targetConsumer", span: "full" },
      { id: "claims", label: "Key claims", type: "tags", placeholder: "Zero sugar, Natural caffeine, No artificial colors", brandKey: "claims" },
      { id: "pricePoint", label: "Retail price point", type: "text", placeholder: "$2.99 per can", brandKey: "pricePoint" },
      { id: "personality", label: "Brand personality", type: "tags", placeholder: "Confident, Clean, Energetic", brandKey: "personality" },
      { id: "axisChoice", label: "Axes", type: "select", options: ["Let the AI choose the most revealing axes", "Price vs. functional benefit", "Natural / clean vs. performance", "Indulgent vs. healthy", "Mass vs. niche / lifestyle", "Custom (describe below)"], default: "Let the AI choose the most revealing axes" },
      { id: "customAxes", label: "Custom axes (optional)", type: "text", placeholder: "X: sweetness level, Y: caffeine strength" },
      { id: "mapCount", label: "Number of maps", type: "select", options: ["1", "2", "3"], default: "2" },
    ],
    template: `Act as a senior brand strategist who has built positioning maps for {{category|lower}} brands and presented them to retail buyers and investors.

Build {{mapCount}} positioning map{{#if mapCount!=1}}s{{/if}} for {{brandName}}, {{category|article}}.

OUR INTENDED POSITIONING
- Positioning: {{positioning}}
{{#if targetConsumer}}- Target consumer: {{targetConsumer}}
{{/if}}{{#if claims}}- Claims: {{claims|join}}
{{/if}}{{#if pricePoint}}- Price point: {{pricePoint}}
{{/if}}{{#if personality}}- Personality: {{personality|join}}
{{/if}}

BRANDS TO PLACE
{{competitors|list}}
- {{brandName}} (place us where we are today AND where we want to be, if different)

AXES
{{axisChoice}}{{#if customAxes}}: {{customAxes}}{{/if}}
{{#if axisChoice=Let the AI choose the most revealing axes}}
Choose axes that (a) consumers actually use to decide, (b) separate the competitors rather than clustering them, and (c) expose defensible whitespace. Reject axes where every brand lands in the same quadrant.
{{/if}}

DELIVERABLES
1. Axis selection: for each map, the X and Y axis, what the extremes mean, and why these axes are decision-relevant for {{targetConsumer|default:the target consumer}}
2. Placement table per map: one row per brand with X score (-5 to +5), Y score (-5 to +5), and the evidence for the placement (pricing, claims, pack design, marketing, with sources)
3. Chart-ready data per map: a plain table (brand, X, Y) that pastes into Google Sheets to plot as a scatter chart
4. Text rendering of each map: a simple 5x5 grid in a code block with brand initials in their quadrants
5. Clusters and whitespace: which brands crowd together, which quadrants are empty, and whether each empty quadrant is an opportunity or a graveyard (explain why no one is there)
6. Recommended position for {{brandName}}: the quadrant, a one-sentence positioning statement, and how it differs from the nearest two competitors
7. Proof points: 5 product, ingredient, price or brand facts {{brandName}} can use to own that position, and 3 proof points we still need to build
8. Messaging ladder: the single-minded claim, 3 reasons to believe, and the tone that fits the position
9. Buyer-ready summary: 4 sentences a retail buyer would accept as the reason this brand deserves a slot the competitors do not fill

RULES
- Base placements on evidence (prices, on-pack claims, marketing); cite sources for competitor facts and label inferences.
- Do not flatter us: if our current product does not support the intended position, say so and explain the gap.
- Flag any proof point that depends on a health, functional or comparative claim for regulatory review.`,
    tips: [
      "Paste the chart-ready data (#3) into Google Sheets and insert a scatter chart — instant map for your deck.",
      "Follow up: “Rewrite our homepage headline and subhead to match the recommended position.”",
      "Run Research Competitors first if you want the placements grounded in fresh, cited facts.",
    ],
  },

  {
    id: "research-survey-builder",
    category: "research",
    title: "Build Consumer Survey",
    description: "A ready-to-launch consumer survey — screener, 15 questions with answer options and skip logic, sample-size guidance and an analysis plan.",
    outputType: "text",
    recommendedTools: ["chatgpt", "claude", "gemini"],
    estimatedTime: "2 min to fill · 1 min in ChatGPT",
    fields: [
      { id: "brandName", label: "Brand name", type: "text", placeholder: "XYZ", required: true, brandKey: "brandName" },
      { id: "category", label: "Product category", type: "text", placeholder: "Energy drink", required: true, brandKey: "category" },
      { id: "productDescription", label: "Product description", type: "textarea", placeholder: "12 oz sleek can, zero sugar, 200 mg natural caffeine", required: true, brandKey: "productDescription", span: "full" },
      { id: "targetConsumer", label: "Target consumer", type: "textarea", placeholder: "Men and women 18–35 interested in fitness", required: true, brandKey: "targetConsumer", span: "full" },
      { id: "positioning", label: "Brand positioning", type: "textarea", placeholder: "Natural premium performance energy", brandKey: "positioning", span: "full" },
      { id: "claims", label: "Key claims", type: "tags", placeholder: "Zero sugar, Natural caffeine, No artificial colors", brandKey: "claims" },
      { id: "pricePoint", label: "Price point to test", type: "text", placeholder: "$2.99 per can", brandKey: "pricePoint" },
      { id: "competitors", label: "Competitors", type: "tags", placeholder: "Celsius, Alani Nu, Ghost", brandKey: "competitors" },
      { id: "surveyGoal", label: "Survey goal", type: "select", options: ["Concept validation (would they buy it?)", "Pricing (what will they pay?)", "Packaging preference (A vs. B)", "Flavor and format preferences", "Brand awareness and perception", "Purchase drivers and barriers"], default: "Concept validation (would they buy it?)" },
      { id: "questionCount", label: "Number of questions", type: "select", options: ["10", "15", "20"], default: "15" },
      { id: "platform", label: "Survey platform", type: "select", options: ["Google Forms", "Typeform", "SurveyMonkey", "Pollfish (paid panel)", "Instagram / email to my own list", "Other"], default: "Google Forms" },
      { id: "decision", label: "Decision this survey will inform", type: "text", placeholder: "Whether to launch flavor #3 at $3.49", span: "full" },
    ],
    template: `Act as a senior quantitative consumer researcher who designs concept, pricing and packaging surveys for emerging {{category|lower}} brands and knows how to avoid leading questions and biased samples.

Design a {{questionCount}}-question consumer survey for {{brandName}}, {{category|article}}. Goal: {{surveyGoal}}. Platform: {{platform}}.
{{#if decision}}
Decision the survey must inform: {{decision}}
{{/if}}

PRODUCT
{{productDescription}}

TARGET RESPONDENT
{{targetConsumer}}

WHAT TO TEST
- Brand: {{brandName}}, {{category|lower}}
{{#if positioning}}- Positioning: {{positioning}}
{{/if}}{{#if claims}}- Claims: {{claims|join}}
{{/if}}{{#if pricePoint}}- Price: {{pricePoint}}
{{/if}}{{#if competitors}}- Competitors to benchmark against: {{competitors|join}}
{{/if}}

DELIVERABLES
1. Research objectives: 3–4 questions the survey must answer, mapped to the decision
2. Screener (3–5 questions) that qualifies {{targetConsumer}} and screens out non-buyers of {{category|lower}}, with the disqualify logic stated
3. The {{questionCount}} main questions, in order, each with: question number, exact wording, question type (single select, multi select, 5-point scale, ranking, open text, Van Westendorp price question, etc.), answer options, and skip / display logic where needed
4. A concept statement block (80–120 words, neutral tone, no superlatives) to show before the purchase-intent questions
5. Purchase-intent question on the standard 5-point scale and how to read the result (top-two-box benchmark for {{category|lower}})
{{#if surveyGoal=Pricing (what will they pay?)}}6. Van Westendorp price-sensitivity block (4 questions) and a Gabor-Granger ladder anchored around {{pricePoint|default:the planned price}}, with analysis instructions
{{/if}}{{#if surveyGoal!=Pricing (what will they pay?)}}6. One price-reaction question anchored around {{pricePoint|default:the planned price}} so the results stay grounded in willingness to pay
{{/if}}7. Demographics block (end of survey, optional fields, inclusive answer options)
8. Sample-size guidance: minimum n for a directional read and for a 95% confidence / ±5% read, how to recruit on {{platform}}, incentive suggestions and how to avoid a friends-and-family bias
9. Analysis plan: for each objective, which questions answer it, what cut or cross-tab to run, and the threshold that means go / no-go
10. Bias checklist: confirm no leading wording, randomized answer order where needed, balanced scales, and one attention-check question
11. A copy-paste-ready version formatted for {{platform}} (question, type, options on separate lines)

RULES
- Neutral wording; never imply the "right" answer.
- Keep the survey under 7 minutes to complete.
- Do not fabricate benchmark numbers; if you cite a category norm, name the source or label it a rule of thumb.
- Flag any question about health effects or medical conditions — it may require a disclaimer or be better left out.`,
    tips: [
      "Paste section 11 straight into Google Forms or Typeform. Run a 5-person test first to catch confusing questions.",
      "Follow up: “Here are my results as a CSV — analyze them against the analysis plan and tell me go or no-go.”",
      "Pollfish or Prolific can deliver 200 qualified respondents in a day if your own list is small.",
    ],
  },
];
